import React, { useMemo, Fragment, FunctionComponent, useCallback, useEffect } from 'react';
import { useTranslation } from '@rbx/intl';
import { Typography, ExpandMoreIcon, NavigateNextIcon, Grid } from '@rbx/ui';
import HeadingItem from '@modules/utilities/interfaces/HeadingItem';
import { traverseTree } from '@modules/ui/shared/utils/tree';
import Tree from '@modules/ui/shared/components/Tree';
import { rightNavID, rightNavWidth } from '@modules/layout/layout/constants/layoutConstants';
import { useAppConfig } from '@modules/layout/layout/components/AppConfigProvider';
import useAppRightNavContentStyles from './AppRightNavContent.styles';
import useAppMainConfig from '../hooks/useAppMainConfig';
import { TreeNodeWithHeading } from '../AppMainConfigContext';
import useElementsShowInView from '../hooks/useElementsShowInView';

export type RenderItem = (item: HeadingItem) => JSX.Element;
export type ExpandableInfo = { nodeId: string; expanded: boolean };

interface AppRightNavContentProps {
  allowExpand?: boolean;
  renderItem?: RenderItem;
}

const defaultRenderItem: RenderItem = ({ title }) => <Fragment>{title}</Fragment>;

const AppRightNavContent: FunctionComponent<React.PropsWithChildren<AppRightNavContentProps>> = ({
  allowExpand = true,
  renderItem = defaultRenderItem,
}) => {
  const { translate } = useTranslation();
  const { showSecondaryNavigation, enableNewLayout } = useAppConfig();
  const {
    classes: { container, content, emptyNode, tree: treeStyle, title },
  } = useAppRightNavContentStyles({
    showSecondaryNavigation,
    enableNewLayout: enableNewLayout ?? false,
  });
  const { pause, disable, enable } = useElementsShowInView();
  const {
    rightNav: { treeNode, isEmpty, expandableInfos, expanded, setExpanded, selected, setSelected },
  } = useAppMainConfig();

  if (isEmpty) throw new Error('Right nav is empty. Do not render this component.');

  const pauseHandleScroll = useCallback(() => pause(200), [pause]);

  const treeWithStyle = useMemo(() => {
    if (isEmpty || treeNode === null) {
      return null;
    }
    if (renderItem) {
      traverseTree(treeNode, (node) => {
        if (node.props === undefined) {
          // eslint-disable-next-line no-param-reassign
          node.props = {};
        }
        const { props } = node;
        const { heading, children } = node as TreeNodeWithHeading;
        if (heading) {
          props.label = renderItem(heading);
        }

        const setExpandCollapseIcons = () => {
          props.collapseIcon = <ExpandMoreIcon />;
          props.expandIcon = <NavigateNextIcon />;
        };

        // In Engine Reference, the heading.level 2 headings are Summary, Properties, Methods, Events, and Callbacks
        // If there are more than 2 children, show expand/collapse icons
        // If there are only 2 child and 1st title is not "Inherited from Instance" and 2nd title is not "Inherited from Object", show expand/collapse icons
        const getInheritedFromTranslated = (name: string) =>
          translate('Label.InheritedFromName', { name });
        const inheritedFromInstanceTranslated = getInheritedFromTranslated('Instance');
        const inheritedFromObjectTranslated = getInheritedFromTranslated('Object');
        const hasChildrenNotInheritedFromInstanceOrObject =
          children?.length > 2 ||
          (children?.length === 2 &&
            children[0].heading?.title !== inheritedFromInstanceTranslated &&
            children[1].heading?.title !== inheritedFromObjectTranslated);
        if (allowExpand && heading?.level === 2 && hasChildrenNotInheritedFromInstanceOrObject) {
          setExpandCollapseIcons();
        }
        // For example, see classes/HumanoidController has "Inherited from Controller"
        if (expandableInfos.find((info) => info.nodeId === node.id)) {
          setExpandCollapseIcons();
        }
        // the content will be scrolled to the anchor (#path) on clicking the node,
        // it will trigger onscroll event on ElementsShowInViewProvider which we dont want
        // do a short period of disabling onscroll event here
        props.onClick = pauseHandleScroll;
      });
    }
    // hide the root node, which is heading.level === 1
    treeNode.props = { classes: { root: emptyNode } };

    return treeNode;
  }, [
    translate,
    isEmpty,
    treeNode,
    renderItem,
    emptyNode,
    allowExpand,
    expandableInfos,
    pauseHandleScroll,
  ]);

  const setExpandedMemoized = useCallback(
    (newExpanded: string[]) => {
      // don't show the expand/collapse icons for disabled expand, such as guides
      if (allowExpand) {
        setExpanded(newExpanded);
      }
    },
    [allowExpand, setExpanded],
  );

  useEffect(() => {
    enable();
    return disable;
    // only trigger on mount. note that setting enable and disable as dependencies will cause infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!treeWithStyle || !treeWithStyle.children || treeWithStyle.children.length === 0) {
    return null;
  }

  return (
    <Grid id={rightNavID} item XSmall={3} Medium Large={4} height='100%' className={container}>
      <Grid
        className={content}
        container
        minWidth={rightNavWidth}
        maxWidth={rightNavWidth}
        paddingLeft='16px'
        direction='column'>
        <Grid item>
          <Typography
            component='h4'
            variant='overline'
            color='secondary'
            className={title}
            paddingLeft={1.5}>
            {translate('Heading.OnThisPage') || 'On this page'}
          </Typography>
        </Grid>
        <Grid item flexGrow={1}>
          <Tree
            className={treeStyle}
            expanded={expanded}
            selected={selected}
            setSelected={setSelected}
            setExpanded={setExpandedMemoized}
            defaultCollapseIcon={false}
            defaultExpandIcon={false}
            tree={treeWithStyle}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

export default AppRightNavContent;
